// The line Show solution plays: not only the puzzle's first move but the
// engine's line from the position, as far as the puzzle's reason is realised
// (the review API's `mainLineOf`, which the step controls' Main line button
// plays too; the walk and its stopping rules moved there on 2026-10-14).
//
// The stored puzzle keeps a short line: one move for a blunder replayed
// ("find the better move") and for a winning advantage, the settled material
// for a captured prize. The scan keeps more: the engine's principal variation
// from the position (`lines[0].pv`), and that is what the solution walks when
// it begins with the puzzle's move; the puzzle's own line otherwise. The
// target is the rule that made the puzzle (`missGapPawns` for a missed
// opportunity, `blunderPawns` for a blunder replayed); a mate prize plays to
// the mate or the line's end.
import { mainLineOf, type MainLine, type MainLineReason, type MainLineRules } from '@chess/api-review';
import type { MistakeRules, Uci } from '@chess/api-model';
import type { Puzzle } from './finder.ts';

export type SolutionReason = MainLineReason;
export type SolutionLine = MainLine;

/** The rules of chess the line needs: to play a move and read the position after it. */
export type SolutionRules = MainLineRules;

/** The rules that made the puzzle: the gap behind a miss and the loss behind a blunder, in pawns. */
export type SolutionGaps = Pick<MistakeRules, 'blunderPawns' | 'missGapPawns'>;

/** The line to walk: the scan's pv when it begins as the puzzle does and is no shorter, else the puzzle's own line. */
export function lineToShow(puzzle: Pick<Puzzle, 'line'>, pv: Uci[] | undefined): Uci[] {
  if (pv && pv.length >= puzzle.line.length && pv[0] === puzzle.line[0]) return pv;
  return puzzle.line;
}

/**
 * The moves Show solution plays for an engine puzzle and why they stop
 * there. `pv` is the scan's best line from the puzzle's position, when the
 * scan has it; `gaps` the mistake rules the puzzle was found under.
 */
export function solutionLineOf(puzzle: Pick<Puzzle, 'kind' | 'side' | 'fen' | 'line' | 'prize'>, pv: Uci[] | undefined, gaps: SolutionGaps, chess: SolutionRules): SolutionLine {
  const target = puzzle.prize.kind === 'mate' ? 'mate' : puzzle.kind === 'tactic' ? gaps.missGapPawns : gaps.blunderPawns;
  return mainLineOf({ fen: puzzle.fen, side: puzzle.side, line: lineToShow(puzzle, pv), target }, chess);
}
